import {
  Check,
  CircleSlash,
  AlertTriangle,
  Trash2,
  Loader2,
  PackagePlus,
  Pencil,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { StockArticle } from "@/lib/stock.api";
import {
  getArticlePotentialGain,
  getArticleStockValue,
  getSellingPrice,
  getSupplierPrice,
  getUnitMargin,
  isLowStock,
} from "../stock-calculations";
import { formatMoney } from "../stock-formatters";
import { DetailRow, DetailStat } from "./StockDetailPrimitives";
import { StockArticleStatusBadge } from "./StockBadges";

interface ArticleDetailDialogProps {
  article: StockArticle | null;
  canManage: boolean;
  toggling: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleActive: (article: StockArticle) => void;
  canDelete?: boolean;
  deleting?: boolean;
  onDelete?: (article: StockArticle) => void;
  onEdit?: (article: StockArticle) => void;
  onRestock?: (article: StockArticle) => void;
}

export function ArticleDetailDialog({
  article,
  canManage,
  toggling,
  onOpenChange,
  onToggleActive,
  canDelete,
  deleting,
  onDelete,
  onEdit,
  onRestock,
}: ArticleDetailDialogProps) {
  const lowStock = article ? isLowStock(article) : false;

  return (
    <Dialog open={Boolean(article)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-2xl">
        {article && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    {article.name}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    Article #{article.id} - unité {article.unit}
                  </DialogDescription>
                </div>
                <StockArticleStatusBadge article={article} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <DetailStat
                label="En stock"
                value={`${article.onHand} ${article.unit}`}
                danger={lowStock}
              />
              <DetailStat label="Seuil bas" value={`${article.minLevel} ${article.unit}`} />
              <DetailStat label="Valeur stock" value={formatMoney(getArticleStockValue(article))} />
              <DetailStat
                label="Gain potentiel"
                value={formatMoney(getArticlePotentialGain(article))}
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="border-b border-border bg-surface-muted px-4 py-2">
                <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Informations article
                </p>
              </div>
              <table className="w-full text-left">
                <tbody>
                  <DetailRow label="Nom" value={article.name} />
                  <DetailRow label="Unité" value={article.unit} />
                  <DetailRow label="Prix fournisseur" value={formatMoney(getSupplierPrice(article))} />
                  <DetailRow label="Prix vente" value={formatMoney(getSellingPrice(article))} />
                  <DetailRow label="Marge unitaire" value={formatMoney(getUnitMargin(article))} />
                  <DetailRow label="Statut stock" value={lowStock ? "Sous le seuil" : "Correct"} />
                </tbody>
              </table>
            </div>

            {lowStock && (
              <div className="rounded-lg border border-warning-border bg-warning-bg px-4 py-3">
                <p className="flex items-center gap-2 text-[12px] font-medium text-warning-text">
                  <AlertTriangle size={14} />
                  Cet article est sous son seuil bas.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              {canManage && (
                <>
                  <Button variant="outline" onClick={() => onEdit?.(article)}>
                    <Pencil size={13} /> Modifier
                  </Button>
                  <Button variant="outline" onClick={() => onRestock?.(article)}>
                    <PackagePlus size={13} /> Réapprovisionner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onToggleActive(article)}
                    disabled={toggling}
                  >
                    {article.active ? <CircleSlash size={13} /> : <Check size={13} />}
                    {article.active ? "Désactiver" : "Activer"}
                  </Button>
                </>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  onClick={() => onDelete?.(article)}
                  disabled={deleting}
                  className="text-danger-text hover:bg-danger-bg"
                >
                  {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
